import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ClmPortalIncomingPostMessageTypes } from 'utils/postMessageUtil';
import { JbNavigationWizardTabListLayout, JbNavigationWizardWithLayout } from '@bjb-gcrm/gcrm-frontend-components';
import EmbeddedIFrame from 'views/Frames/EmbeddedIFrame';
import { useRuntimeConfig } from 'config/useRuntimeConfig';
import { InstructionsTab } from 'views/NavigationWizard/components/InstructionsTab';
import { NavigationContent } from 'views/NavigationWizard/components/NavigationContent';
import { BrTabItems } from 'views/BrTabs/BrTabs';
import { NavigationWizardModeStateMachineOutput } from 'views/BrTabs/types/navigationWizardMode';
import { ChangeHistoryAction } from 'views/NavigationWizard/components/ChangeHistoryAction';

const tabList = [
  { id: 'navigation', title: 'Navigation' },
  { id: 'instructions', title: 'Instructions' },
];

type Props = {
  url: string;
  title: string;
  onNavigate: (tabId: BrTabItems) => void;
  id: string;
  navigationWizardMode?: NavigationWizardModeStateMachineOutput;
};

export const NavigationWizard = ({ url, title, onNavigate, id, navigationWizardMode = { type: 'full' } }: Props) => {
  const { t } = useTranslation();
  const runtimeConfig = useRuntimeConfig();
  const [drawerExpanded, setDrawerExpanded] = useState(true);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.message === ClmPortalIncomingPostMessageTypes.CLOSE_DRAWER) {
        setDrawerExpanded(false);
      } else if (event.data?.message === ClmPortalIncomingPostMessageTypes.OPEN_DRAWER) {
        setDrawerExpanded(true);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const env = runtimeConfig.envInfo.envId;
  const showInstructions = runtimeConfig.configData.featureFlags?.showInstructions?.enabled;

  const renderTab = (tabId: string): ReactNode => {
    switch (tabId) {
      case 'navigation':
        return <NavigationContent env={env} onNavigate={onNavigate} navigationWizardMode={navigationWizardMode} />;
      case 'instructions':
        return <InstructionsTab env={env} />;
    }
  };

  const left = showInstructions ? (
    <JbNavigationWizardTabListLayout tabList={tabList} renderTab={renderTab} />
  ) : (
    <NavigationContent env={env} onNavigate={onNavigate} navigationWizardMode={navigationWizardMode} />
  );

  return (
    <JbNavigationWizardWithLayout
      title={t('navigationWizard.title')}
      left={left}
      headerAction={<ChangeHistoryAction env={env} />}
      right={<EmbeddedIFrame url={url} title={title} id={id} centered />}
      drawerExpanded={drawerExpanded}
      onDrawerExpandedChange={setDrawerExpanded}
    />
  );
};
